import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { MessageSquare, UserRound } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { conversationsStreamUrl, fetchConversations, fetchConversationDetail, fetchStats } from '../../api'
import { useAuth } from '../../AuthContext'
import { PageHeader } from '../../components/PageHeader'
import { SociedadScope } from '../../components/SociedadPicker'
import { StatsCards } from '../../components/StatsCards'
import { ConversationList } from '../../components/ConversationList'
import { ConversationDetail } from '../../components/ConversationDetail'

const FALLBACK_POLL_INTERVAL_MS = 30000

function playHandoffSound(): void {
  try {
    const ctx = new AudioContext()
    const oscillator = ctx.createOscillator()
    const gain = ctx.createGain()
    oscillator.type = 'sine'
    oscillator.frequency.value = 880
    gain.gain.setValueAtTime(0.15, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4)
    oscillator.connect(gain)
    gain.connect(ctx.destination)
    oscillator.start()
    oscillator.stop(ctx.currentTime + 0.4)
  } catch {
    // Los navegadores exigen interacción previa del usuario para permitir audio;
    // si falla, el toast visual sigue siendo suficiente aviso.
  }
}

function ConversationsView({ sociedadId }: { sociedadId: string }) {
  const { phone: selectedPhone } = useParams<{ phone?: string }>()
  const navigate = useNavigate()
  const [handoffToast, setHandoffToast] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { accessToken, refreshSession } = useAuth()
  const isCurrentEffectRef = useRef(true)

  useEffect(() => {
    if (!accessToken) return
    isCurrentEffectRef.current = true
    let source = new EventSource(conversationsStreamUrl(accessToken, sociedadId))

    function handleMessage(event: MessageEvent<string>) {
      queryClient.invalidateQueries({ queryKey: ['conversations', sociedadId] })
      queryClient.invalidateQueries({ queryKey: ['stats', sociedadId] })
      queryClient.invalidateQueries({ queryKey: ['conversation'] })
      queryClient.invalidateQueries({ queryKey: ['conversation-messages'] })

      if (event.data === 'handoff_requested') {
        setHandoffToast('Un cliente pidió hablar con un agente humano.')
        playHandoffSound()
      }
    }

    function handleError() {
      source.close()
      refreshSession().then((freshToken) => {
        if (!freshToken || !isCurrentEffectRef.current) return
        source = new EventSource(conversationsStreamUrl(freshToken, sociedadId))
        source.onmessage = handleMessage
        source.onerror = handleError
      })
    }

    source.onmessage = handleMessage
    source.onerror = handleError

    return () => {
      isCurrentEffectRef.current = false
      source.close()
    }
  }, [queryClient, accessToken, sociedadId, refreshSession])

  useEffect(() => {
    if (!handoffToast) return
    const timeout = setTimeout(() => setHandoffToast(null), 6000)
    return () => clearTimeout(timeout)
  }, [handoffToast])

  const statsQuery = useQuery({
    queryKey: ['stats', sociedadId],
    queryFn: fetchStats,
    refetchInterval: FALLBACK_POLL_INTERVAL_MS,
  })

  const conversationsQuery = useQuery({
    queryKey: ['conversations', sociedadId],
    queryFn: fetchConversations,
    refetchInterval: FALLBACK_POLL_INTERVAL_MS,
  })

  const detailQuery = useQuery({
    queryKey: ['conversation', sociedadId, selectedPhone],
    queryFn: () => fetchConversationDetail(selectedPhone!),
    enabled: !!selectedPhone,
    refetchInterval: selectedPhone ? FALLBACK_POLL_INTERVAL_MS : false,
  })

  return (
    <>
      <AnimatePresence>
        {handoffToast && (
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            className="fixed left-1/2 top-4 z-50 flex -translate-x-1/2 items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-medium text-white shadow-lg shadow-amber-500/30"
          >
            <UserRound className="h-4 w-4" />
            {handoffToast}
          </motion.div>
        )}
      </AnimatePresence>

      <StatsCards stats={statsQuery.data} isLoading={statsQuery.isLoading} />

      <div className="grid min-h-[480px] flex-1 grid-cols-1 gap-6 lg:grid-cols-[360px_1fr]">
        <ConversationList
          conversations={conversationsQuery.data}
          isLoading={conversationsQuery.isLoading}
          isError={conversationsQuery.isError}
          selectedPhone={selectedPhone ?? null}
          onSelect={(phone) => navigate(`/app/conversaciones/${encodeURIComponent(phone)}`)}
        />
        <ConversationDetail
          conversation={detailQuery.data}
          isLoading={detailQuery.isLoading}
          isError={detailQuery.isError}
          hasSelection={!!selectedPhone}
        />
      </div>
    </>
  )
}

export function ConversationsPage() {
  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <PageHeader icon={MessageSquare} title="Conversaciones" description="Mensajes en tiempo real con tus contactos." />
      <SociedadScope>{(sociedadId) => <ConversationsView sociedadId={sociedadId} />}</SociedadScope>
    </div>
  )
}
